import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { TabMenuComponent } from './tab-menu/tab-menu.component';
import { CardModule } from 'primeng/card';
import { DataBoxComponent } from '@shared/components/data-box/data-box.component';
import { FlightTotalsInfoComponent } from './flight-list/flight-totals-info/flight-totals-info.component';
import { RouterOutlet } from '@angular/router';
import { DataItem } from '@shared/models/data-item';
import { MonthsShort } from '@shared/enums/months-short';
import { SignalsService } from '@shared/services/signals/signals.service';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { FlightTotalsService } from './services/flight-totals.service';
import { combineLatest, EMPTY, map, merge, Observable, of, switchMap } from 'rxjs';
import { formatTime } from '@shared/utilities/utilities';
import { SearchService } from '../search/search.service';
import { FieldsetModule } from 'primeng/fieldset';
import { SkeletonModule } from 'primeng/skeleton';
import { FlightsService } from './flight-list/flight.service';
import { FlightTotals } from './flight-totals';
import { RequestState } from '@shared/models/request-state';
import { ChartService } from '../analyze/chart/chart.service';
import { ComboChart } from '../analyze/chart/model/combo-chart';
import { SummaryPeriod } from '@shared/models/summary-period';
import { MonthsLong } from '@shared/enums/months-long';
import { CalendarService } from './flights-calendar/services/calendar.service';

interface TotalsDataItem {
  year: string,
  month: string,
}

@Component({
  selector: 'app-flights',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    CardModule,
    TabMenuComponent,
    DataBoxComponent,
    ToastModule,
    FieldsetModule,
    SkeletonModule
  ],
  templateUrl: './flights.component.html',
  styleUrl: './flights.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightsComponent implements OnInit, OnDestroy {
  constructor(
    private flightTotalsService: FlightTotalsService,
    private signalService: SignalsService,
    private searchService: SearchService,
    private chartService: ChartService,
    private flightsService: FlightsService,
    private calendarService: CalendarService
  ) { }

  sub = this.flightTotalsService.isRefresh$.subscribe((refresh) => {
    if (refresh) {
      this.loadFlightTotalsData(true);
    }
  })

  currentYear = new Date().getFullYear();
  currentPeriod = new Date().getMonth();

  flightTotalsInfoDialog = FlightTotalsInfoComponent;
  empNumber$ = this.signalService.employeeNumber$;
  flightTotalsData$?: Observable<DataItem[]>;

  headerTitle = 'Flight Totals';
  skeletonData: DataItem[] = this.loadSkeletonData();



  ngOnInit(): void {
    sessionStorage.removeItem('sequence');
    sessionStorage.removeItem('leg');
    sessionStorage.removeItem('chartId');
    sessionStorage.removeItem('form');
    sessionStorage.removeItem('searchResponse');
    sessionStorage.removeItem('nonFilteredSequence');
    sessionStorage.removeItem('nonFilteredResults');
    this.searchService.clearOldSearchResultsCache();
    this.loadFlightTotalsData();
  }


  dialogFooterFlightTotals =
    'Does not include Deadhead or Cancelled flights.';

  /**
   * Loads and combines flight statistics data to create a formatted display of flight totals.
   * 
   * This method combines three data streams:
   * - Flight statistics data
   * - Monthly line chart combo data
   * - Summary period information
   * 
   * The combined data is transformed into an array of DataItem objects containing:
   * - Total flight time
   * - Current year's flight time
   * - Current month's flight time
   * 
   * @param {boolean} [isRefresh] - Optional flag to indicate if this is a data refresh operation
   * @returns {Observable<DataItem[]>} An observable of DataItem array containing formatted flight totals
   */
  loadFlightTotalsData(isRefresh?: boolean): void {
    this.flightTotalsData$ = combineLatest([
      this.loadFlightStatsData(isRefresh),
      this.loadByMonthLineChartComboData(),
      this.getSummaryPeriodSource()
    ]).pipe(
      map(([flightStats, byMonth, selectedPeriod]) => {
        let dataItem: DataItem[] = [];
        if (flightStats.isLoading || (!flightStats.response || !selectedPeriod || !byMonth)) {
          return dataItem;
        }
        this.flightTotalsService._isRefresh.next(false);
        console.log("%cFlight Totals updated", "color: lightgreen");
        dataItem = [
          {
            label: 'Total',
            value: formatTime(flightStats.response.totalTime)
          },
          {
            label: String(selectedPeriod.year),
            value: this.getCurrentTimeValues(selectedPeriod, byMonth).year
          },
          {
            label: String(MonthsLong[selectedPeriod.period - 1]),
            value: this.getCurrentTimeValues(selectedPeriod, byMonth).month
          }
        ];
        return dataItem;
      })
    );
  }

  loadFlightStatsData(isRefresh?: boolean): Observable<RequestState<FlightTotals>> {
    return this.empNumber$.pipe(
      switchMap((empNum) => {
        if (isRefresh) {
          localStorage.removeItem(`${empNum}-fltStats`);
          localStorage.removeItem(`${empNum}-chartCombo`);
        }
        return this.flightTotalsService.getFlightTimeStats(empNum, isRefresh)
      })
    );
  }

  loadByMonthLineChartComboData(): Observable<ComboChart[]> {
    return this.chartService.getByMonthLineChartCombo();
  }

  /**
   * Calculates and formats the total time values for a given period from chart data
   * @param period - The summary period containing year and period (month) information
   * @param data - Array of combo chart data containing yearly and monthly flight time data
   * @returns {TotalsDataItem} Object containing formatted year and month times in "HH:MM" format
   * - If no data found for year: returns "00:00" for both year and month
   * - If year data exists but no month match: returns formatted year total and "00:00" for month
   * - If both year and month exist: returns both formatted totals
   */
  getCurrentTimeValues(period: SummaryPeriod, data: ComboChart[]): TotalsDataItem {
    const selectedYear = data.find((year) => year.year === period.year);

    if (!selectedYear) {
      return { year: '00:00', month: '00:00' };
    }

    const matchYear = selectedYear.months.data.reduce((acc, month) => {
      return acc + month;
    }, 0);

    let selectedMonthData: number = 0;
    const matchMonth = selectedYear.months.label.find((month, idx) => {
      if (month === period.period) {
        selectedMonthData = selectedYear.months.data[idx];
        return month;
      }
      else {
        return null;
      }
    });
    if (matchYear === 0 && (!matchMonth || matchMonth === null)) {
      return { year: '00:00', month: '00:00' };
    }
    else if (matchYear && (!matchMonth || matchMonth === undefined)) {
      const formattedTimeYear = this.convertMinutesToHours(matchYear);
      return { year: formattedTimeYear, month: '00:00' };
    }
    else {
      const formattedTimeYear = this.convertMinutesToHours(matchYear);
      const formattedTimeMonth = this.convertMinutesToHours(selectedMonthData);
      return { year: formattedTimeYear, month: formattedTimeMonth };
    }
  }

  /**
   * Returns an Observable that emits SummaryPeriod values from merged flight and calendar services
   * and determines the source the change orginated from: Table View or Calendar View.
   * Combines selected month streams from both services and filters out null values.
   * 
   * @returns Observable<SummaryPeriod | null> - Stream of SummaryPeriod values or null
   */
  getSummaryPeriodSource(): Observable<SummaryPeriod | null> {
    return merge(
      this.flightsService.selectedMonth$,
      this.calendarService.selectedMonth$
    ).pipe(
      switchMap(value => value ? of(value) : EMPTY)
    );
  }

  convertMinutesToHours(minutesTime: number): string {
    const timeInHours = minutesTime;
    const hours = Math.floor(timeInHours);
    const minutes = Math.round((timeInHours - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  loadSkeletonData(): DataItem[] {
    return [
      {
        label: 'Total',
        value: '00:00'
      },
      {
        label: String(this.currentYear),
        value: '00:00'
      },
      {
        label: String(MonthsShort[this.currentPeriod]),
        value: '00:00'
      }
    ];
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

}
